import React from "react"

import * as styles from "../styles"
import Tooltip from "./Tooltip"
import { AnchorPoint } from "./ColorPickerPopover"
import { ColorTheme, Fonts, fontFamilyExpr } from "../visualProfile"
import { sanitizeSvg } from "utils/Validation"

export type ClickTarget =
  | { kind: "background_color" }
  | { kind: "headline_color" }
  | { kind: "body_color" }
  | { kind: "accent_colors"; index: number }
  | { kind: "accent_gradients" }
  | { kind: "logo" }

type QuoteSlideProps = {
  theme: ColorTheme
  fonts: Fonts
  accent: string
  // null when this variant is the gradient (so its accent click resolves to
  // { kind: "accent_gradients" }); the index otherwise.
  accentIndex: number | null
  // Shown as a text wordmark when the theme has no logo — mirrors the carousel's no-logo fallback.
  logoFallbackBrandName: string
  onClick: (target: ClickTarget, point: AnchorPoint) => void
}

// Same post on every slide; only the accent (plain color or gradient) used
// for the highlighted word and the divider changes, so the row reads as
// "what would this same post look like in each of the brand's accents?".
const sampleText = {
  before: "",
  highlight: "Consistency",
  after: " beats brilliance. Post at least once a week.",
  subtext: "Notes from a year of weekly publishing.",
}

const headlineFont = (fonts: Fonts, color: string): React.CSSProperties => ({
  fontFamily: fontFamilyExpr(fonts.headline_font.family),
  fontWeight: fonts.headline_font.weights[0]?.weight ?? 400,
  textTransform: fonts.headline_font.allcaps ? "uppercase" : "none",
  color,
})

const bodyFont = (fonts: Fonts, color: string): React.CSSProperties => ({
  fontFamily: fontFamilyExpr(fonts.body_font.family),
  fontWeight: fonts.body_font.weights[0]?.weight ?? 400,
  color,
})

const isGradient = (value: string): boolean => value.includes("(")

// Mirror the production logo-sizing formula so the preview renders the logo
// at the same proportional size it'll have on the real 1080-wide slide. See
// the visual-generation prompt for the source rules:
//   logo_display_height = clamp(round(180 / aspect_ratio), 72, 160)  (when natural dims known)
//   logo_display_height = 84                                          (default)
// We then scale by the preview ratio (slide 240 / canvas 1080 ≈ 0.222) and
// pass concrete width/height to the <img> / <svg> — necessary because
// extracted SVGs often carry only viewBox (no intrinsic width/height) and
// would otherwise collapse to 0x0 in the browser.
const PREVIEW_SLIDE_WIDTH = 240
const PRODUCTION_CANVAS_WIDTH = 1080
const PREVIEW_SCALE = PREVIEW_SLIDE_WIDTH / PRODUCTION_CANVAS_WIDTH

const computeLogoSize = (theme: ColorTheme): { width: number; height: number } => {
  const naturalWidth = theme.logo_natural_width
  const naturalHeight = theme.logo_natural_height
  const aspectRatio = naturalWidth && naturalHeight ? naturalWidth / naturalHeight : 1
  const productionHeight = naturalWidth && naturalHeight
    ? Math.max(72, Math.min(160, Math.round(180 / aspectRatio)))
    : 84
  return {
    height: Math.max(1, Math.round(productionHeight * PREVIEW_SCALE)),
    width: Math.max(1, Math.round(productionHeight * aspectRatio * PREVIEW_SCALE)),
  }
}

// Render an accent-colored highlight inside the quote. Plain colors apply
// directly; gradients use background-clip:text so the gradient runs through
// the glyphs. The click affordance lives on the wrapping ClickHaloInline.
const HighlightSpan: React.FC<{
  accent: string
  children: React.ReactNode
}> = ({ accent, children }) => {
  const style: React.CSSProperties = isGradient(accent)
    ? {
        background: accent,
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
      }
    : { color: accent }
  return <span style={style}>{children}</span>
}

const renderLogo = (
  logo: string | undefined,
  size: { width: number; height: number },
  logoFallbackBrandName: string,
  fonts: Fonts,
  color: string,
) => {
  if (!logo) {
    // No logo on this theme, fall back to the brand name as a text wordmark, so the slot is never empty.
    if (!logoFallbackBrandName) return <div style={size} />
    return (
      <span style={{ ...headlineFont(fonts, color), fontSize: size.height, lineHeight: 1, whiteSpace: "nowrap" }}>
        {logoFallbackBrandName}
      </span>
    )
  }
  const isInlineSvg = logo.trimStart().startsWith("<svg")
  return isInlineSvg
    ? <styles.BrandSlideLogo style={size} dangerouslySetInnerHTML={{ __html: sanitizeSvg(logo) }} />
    : <styles.BrandSlideLogoImg
        src={logo}
        style={size}
        alt=""
        onError={(event) => { (event.currentTarget as HTMLImageElement).style.display = "none" }}
      />
}

const QuoteSlide: React.FC<QuoteSlideProps> = ({ theme, fonts, accent, accentIndex, logoFallbackBrandName, onClick }) => {
  const logoSize = computeLogoSize(theme)
  const pointOf = (event: React.MouseEvent): AnchorPoint => ({ x: event.clientX, y: event.clientY })
  const handleBackgroundClick = (event: React.MouseEvent) => {
    onClick({ kind: "background_color" }, pointOf(event))
  }
  const handleHeadlineClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    onClick({ kind: "headline_color" }, pointOf(event))
  }
  const handleBodyClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    onClick({ kind: "body_color" }, pointOf(event))
  }
  const handleAccentClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    const target: ClickTarget = accentIndex === null
      ? { kind: "accent_gradients" }
      : { kind: "accent_colors", index: accentIndex }
    onClick(target, pointOf(event))
  }
  const handleLogoClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    onClick({ kind: "logo" }, pointOf(event))
  }

  return (
    <Tooltip content="Click to edit background colour" block interactive={false}>
      <styles.BrandSlide
        style={{ background: theme.background_color, cursor: "pointer" }}
        onClick={handleBackgroundClick}
      >
        <styles.BrandSlideTop>
          <Tooltip content="Click to change logo" block interactive={false}>
            <styles.ClickHalo
              style={{ pointerEvents: "auto" }}
              onClick={handleLogoClick}
            >
              {renderLogo(theme.logo, logoSize, logoFallbackBrandName, fonts, theme.headline_color)}
            </styles.ClickHalo>
          </Tooltip>
        </styles.BrandSlideTop>
        <styles.BrandSlideContent>
          <Tooltip content="Click to edit headline colour" block interactive={false}>
            <styles.ClickHalo onClick={handleHeadlineClick}>
              {/* Override BrandSlideQuote's overflow:hidden (needed for the
                  5-line clamp on real text) so the inner ClickHaloInline's
                  dashed outline isn't clipped at the quote's edges. The
                  editable preview always uses the same short sample text,
                  so the clamp isn't relevant here. */}
              <styles.BrandSlideQuote
                style={{ ...headlineFont(fonts, theme.headline_color), overflow: "visible" }}
              >
                {sampleText.before}
                <Tooltip content="Click to edit accent" interactive={false}>
                  <styles.ClickHaloInline onClick={handleAccentClick}>
                    <HighlightSpan accent={accent}>{sampleText.highlight}</HighlightSpan>
                  </styles.ClickHaloInline>
                </Tooltip>
                {sampleText.after}
              </styles.BrandSlideQuote>
            </styles.ClickHalo>
          </Tooltip>
          <Tooltip content="Click to edit accent" block interactive={false}>
            <styles.ClickHalo
              style={{ padding: "8px 10px", margin: "6px 0" }}
              onClick={handleAccentClick}
            >
              <styles.BrandSlideDivider style={{ background: accent, margin: 0 }} />
            </styles.ClickHalo>
          </Tooltip>
          <Tooltip content="Click to edit body colour" block interactive={false}>
            <styles.ClickHalo onClick={handleBodyClick}>
              <styles.BrandSlideSubline style={{ ...bodyFont(fonts, theme.body_color), opacity: 0.75 }}>
                {sampleText.subtext}
              </styles.BrandSlideSubline>
            </styles.ClickHalo>
          </Tooltip>
        </styles.BrandSlideContent>
      </styles.BrandSlide>
    </Tooltip>
  )
}

export default QuoteSlide
